// tslint:disable: cyclomatic-complexity
import isEmpty from 'lodash/isEmpty';
import Styled, { css, FlattenSimpleInterpolation } from 'styled-components';

import { media } from '../../../../lib/StyledComponents/StyledComponents';
import { SVGIconStyleWrapper } from '../../../Icon/SVGIcon';
import { cssDeclaration, REM_NUM_CONVERSION } from '../../Utils';
import { IFocusControl, IHoveredProps, IInputError, IInputMaxWidth } from '../common';

import { IAdornmentWidth } from './Input';

interface IStyledInputProps {
  hasIcon?: boolean;
  iconPosition?: 'left' | 'right';
  iconSize?: string;
}

interface IStyledInputAdornment {
  adornmentWidth: IAdornmentWidth;
  hasAdornment?: boolean;
}

export type InputCommonFocusControl = IInputError & IFocusControl;

const calcStyledInputPadding = (horizontalPadding: string, position: string, size: string): FlattenSimpleInterpolation => {
  if (horizontalPadding === '0rem') {
    return css`
      padding-${position}:
        calc(${size} + 0.8rem);
    `;
  } else {
    return css`
      padding-${position}:
        calc(${size} + (${horizontalPadding}));
    `;
  }
};

export const BaseInputStyles = css<InputCommonFocusControl & IHoveredProps>`
  width: 100%;
  transition: all 0.1s ease-out;
  text-overflow: ellipsis;
  // below zeros default styles
  outline: none;
  border: none;
  padding: 0;

  ${({ theme, error, isHovered, clickFocus }) => {
    const themeTwo = theme.themeTwo;
    const { input } = theme.themeTwo.form;
    const {
      state: {
        disabled, error: errorState, hover, focus,
      }
    } = input;

    return css`
      background-color: transparent;
      z-index: ${themeTwo.global.zIndex.low};

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus{
        z-index: auto;
      }
      /* color */ ${input.font && cssDeclaration(themeTwo, 'color', input.font.color)};
      /* font-size */ ${input.font && input.font.size && cssDeclaration(themeTwo, 'font-size', input.font.size)};
      /* line-height */ ${input.font && input.font.lineHeight && cssDeclaration(themeTwo, 'line-height', input.font.lineHeight)};
      /* border */ ${cssDeclaration(themeTwo, 'border', input.border)};
      /* border-radius */ ${cssDeclaration(themeTwo, 'border-radius', input.round)};
      /* padding */ ${cssDeclaration(themeTwo, 'padding', input.padding)};
      opacity: ${input.opacity};
      ${input.additionalStyles && input.additionalStyles};

      ${!clickFocus && css`
        &:focus {
          /* color */ ${focus.font && cssDeclaration(themeTwo, 'color', focus.font.color)};
          /* box-shadow */ ${focus.shadow && cssDeclaration(themeTwo, 'box-shadow', focus.shadow)};
          /* border */ ${focus.border && cssDeclaration(themeTwo, 'border', focus.border)};
          /* outline */ ${focus.outline && cssDeclaration(themeTwo, 'outline', focus.outline)};
        }
      `}

      ${isHovered && css`
        /* color */ ${hover.font && cssDeclaration(themeTwo, 'color', hover.font.color)};
        /* box-shadow */ ${hover.shadow && cssDeclaration(themeTwo, 'box-shadow', hover.shadow)};
        /* border */ ${hover.border && cssDeclaration(themeTwo, 'border', hover.border)};
        /* outline */ ${hover.outline && cssDeclaration(themeTwo, 'outline', hover.outline)};
      `}

      &:disabled {
        box-shadow: none;
        cursor: not-allowed;

        /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', disabled.font.color)};
        /* safari fix */ ${disabled.font && cssDeclaration(themeTwo, '-webkit-text-fill-color', disabled.font.color)};
        /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};
        /* background-color */
        ${disabled.font && disabled.backgroundColor &&
          cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)
        };

        &::placeholder {
          /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', disabled.font.color)};
        }

        &:hover {
          box-shadow: none;
          cursor: not-allowed;

           /* background-color */ ${disabled.backgroundColor && cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)};
           /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', disabled.font.color)};
        }
      }

      &::placeholder {
        /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', input.placeholder)};
      }

      // error state
      ${error && css`
        /* color */ ${errorState.font && cssDeclaration(themeTwo, 'color', errorState.font.color)};
        /* border */ ${errorState.border && cssDeclaration(themeTwo, 'border', errorState.border)};
        /* box-shadow */ ${errorState.shadow && cssDeclaration(themeTwo, 'box-shadow', errorState.shadow)};
      `}
    `;

  }}
`;

export const InputContainer = Styled.div<IStyledInputProps & IHoveredProps & IInputMaxWidth>`
  position: relative;
  display: flex;
  align-items: center;

  ${({ theme, iconPosition, isHovered, iconSize, maxWidth }) => {
    const themeTwo = theme.themeTwo;
    const { form: { input, label } } = themeTwo;
    const paddingOffset = themeTwo.global.edgeSize[input.padding.horizontal] || input.padding.horizontal;
    const size = iconSize && themeTwo.global.edgeSize[iconSize] || iconSize;

    return css`
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', input.backgroundColor)};
      /* border-radius */ ${cssDeclaration(themeTwo, 'border-radius', input.round)};

      & > ${SVGIconStyleWrapper} {
        position: absolute;
        height: ${size || '1em'};
        width: ${size || '1em'};

        /* color */ ${cssDeclaration(themeTwo, 'color', input.iconColor)};

        /* defaults to right for select inputs */
        ${iconPosition ? iconPosition : 'right'}: ${paddingOffset};

        & > svg {
          height: 100%;
          width: 100%;
          fill: currentColor;
        }
      }

      ${label.font.color.hover && isHovered && css`
        & > ${SVGIconStyleWrapper} {
          /* color */ ${cssDeclaration(themeTwo, 'color', label.font.color.hover)};
          transition: color 0.1s ease-out;
        }
      `}

      ${maxWidth && css`
        ${media.small`
          max-width: ${maxWidth};
        `}
      `}
    `;
  }}
`;

export const StyledInput = Styled.input<IStyledInputProps & InputCommonFocusControl & IHoveredProps & IStyledInputAdornment>`
  ${BaseInputStyles}

  // styles with icon
  ${({ theme: { themeTwo }, adornmentWidth, hasAdornment, hasIcon, iconPosition, iconSize }) => {
    const { form: { input } } = themeTwo;
    const { state: { readOnly, disabled }} = input;

    const startAdornmentWidth = adornmentWidth.start && `${adornmentWidth.start / REM_NUM_CONVERSION}rem`;
    const endAdornmentWidth = adornmentWidth.end && `${adornmentWidth.end / REM_NUM_CONVERSION}rem`;
    const size = iconSize && themeTwo.global.edgeSize[iconSize] || iconSize;
    const horizontalPadding = themeTwo.global.edgeSize[input.padding.horizontal] || input.padding.horizontal;
    const paddingWithIcon = calcStyledInputPadding(horizontalPadding, iconPosition as string, size as string);
    const paddingWithStartAdornment = startAdornmentWidth && calcStyledInputPadding(horizontalPadding, 'left', startAdornmentWidth) ;
    const paddingWithEndAdornment = endAdornmentWidth && calcStyledInputPadding(horizontalPadding, 'right', endAdornmentWidth) ;

    return css`
      ${hasIcon && paddingWithIcon};

      ${hasAdornment && startAdornmentWidth && paddingWithStartAdornment};
      ${hasAdornment && endAdornmentWidth && paddingWithEndAdornment};

      ${!isEmpty(readOnly) ? css`
        &:read-only {
            box-shadow: none;
            cursor: not-allowed;

            /* color */ ${readOnly.font && cssDeclaration(themeTwo, 'color', readOnly.font.color)};
            /* background-color */ ${readOnly.backgroundColor && cssDeclaration(themeTwo, 'background-color', readOnly.backgroundColor)};
            /* border */ ${readOnly.border && cssDeclaration(themeTwo, 'border', readOnly.border)};

            &::placeholder {
              /* color */ ${readOnly.font && cssDeclaration(themeTwo, 'color', readOnly.font.color)};
            }
          }
      ` : css`
          &:read-only {
            box-shadow: none;
            cursor: pointer;

            /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', disabled.font.color)};
            /* background-color */ ${disabled.backgroundColor && cssDeclaration(themeTwo, 'background-color', disabled.backgroundColor)};
            /* border */ ${disabled.border && cssDeclaration(themeTwo, 'border', disabled.border)};

            &::placeholder {
              /* color */ ${disabled.font && cssDeclaration(themeTwo, 'color', disabled.font.color)};
            }
          }
      `}
    `;
  }};
`;
